import ClearIcon from '@mui/icons-material/Clear'
import SendIcon from '@mui/icons-material/Send'
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from '@mui/material'
import Button from '@mui/material/Button'
import { message, Table, TableColumnsType } from 'antd'
import dayjs from 'dayjs'
import { useFormik } from 'formik'
import { chunk, filter } from 'lodash'
import { useEffect, useState } from 'react'
import CircularLoading from '../../../components/CircularProgress'
import { Tag } from '../../../components/Tag'
import { ERRORS_MESSAGE_MAPPER } from '../../../constants/errors'
import { ONLY_DATE_TIME_FORMAT } from '../../../constants/global'
import { axiosInstance } from '../../../hooks/useAxios'
import { EOrderStatus, IOrder, OrderRecipient } from '../../../types/order'

interface ICancelTrackFormModalProps {
  trackingProvider?: string
  isOpen: boolean
  onClose: (visible: boolean) => void
  orders: IOrder[]
  onUpdateTrackingSuccess: () => void
}

interface ICancelTrackFormModal {
  orders: IOrder[]
}

interface ITracking extends IOrder {
  error?: string
}

export const CancelTrackFormModal = (props: ICancelTrackFormModalProps) => {
  const { trackingProvider, isOpen, onClose, orders } = props
  const [loading, setLoading] = useState<boolean>(false)
  const [isCancelTracking, setIsCancelTracking] = useState<boolean>(false)
  const [popupMessage, setPopupMessage] = useState<string>('')

  useEffect(() => {
    setLoading(true)
    setPopupMessage('')
    formik.setFieldValue(
      'orders',
      orders?.filter(
        (order) => order.trackingCode && order.status === EOrderStatus.TRACKING,
      ),
    )
    setLoading(false)
  }, [isOpen, orders])

  const formik = useFormik<ICancelTrackFormModal>({
    initialValues: {
      orders: [],
    } as ICancelTrackFormModal,
    onSubmit: async (values: ICancelTrackFormModal) => {
      if (values.orders.length === 0) {
        message.error('กรุณาเลือกออเดอร์')
        return
      }

      setLoading(true)

      const ordersTracking = values.orders.filter(
        (order: IOrder) => order.trackingCode,
      )

      const chunkNumber = 5
      const orderChunk = chunk(ordersTracking, chunkNumber)

      let orderResonses: any = []
      let countSuccess = chunkNumber
      for (const orders of orderChunk) {
        try {
          const responseResult = await axiosInstance.post(
            `/trackings/${trackingProvider}/cancel`,
            {
              provider: trackingProvider,
              orders: orders.map((order: IOrder) => order.objectId),
              note: '',
            },
          )
          setPopupMessage(
            'กำลังยกเลิกหมายเลขพัสดุ รายการที่ ' +
              countSuccess +
              '/' +
              ordersTracking.length,
          )
          orderResonses = [...orderResonses, ...responseResult.data]
        } catch (e: any) {
          const messageError =
            ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
            'ทำรายการไม่สำเร็จ'
          message.error(messageError)
        }
        countSuccess += orders.length ?? 0
      }
      setLoading(false)
      const trackingsResponse = orderResonses.reduce((acc: any, cur: any) => {
        acc[cur.order] = cur
        return acc
      }, {})
      const trackings = formik.values.orders.map<ITracking>(
        (order: ITracking) => {
          const tracking = trackingsResponse?.[order.objectId]
          if (!tracking) {
            return order
          }

          let error = ''
          if (!tracking?.success) {
            error =
              tracking?.errors?.message ??
              tracking.errors.validations ??
              'ไม่สามารถยกเลิก Tracking ได้'
          }

          return {
            ...order,
            trackingCode: tracking?.success
              ? 'ยกเลิกสำเร็จ'
              : order.trackingCode,
            error: error,
          }
        },
      )
      await formik.setFieldValue('orders', trackings)
      //onClose(false)
      // onUpdateTrackingSuccess()
    },
  })

  const columns: TableColumnsType<ITracking & { key: number }> = [
    {
      title: 'ชื่อ',
      dataIndex: 'recipient',
      key: 'recipient',
      width: 150,
      render: (recipient: OrderRecipient) => (
        <Grid container spacing={1} flexDirection={'column'}>
          <Grid item>
            <Typography variant="h6" color="text.secondary">
              {recipient.name}
            </Typography>
          </Grid>
          <Grid item>
            <Typography variant="subtitle2" color="text.secondary">
              {recipient.address} {recipient.subDistrict} {recipient.district}{' '}
              {recipient.province} {recipient.zipcode}
            </Typography>
          </Grid>
          <Grid item>
            <Typography variant="subtitle2" color="text.secondary">
              {recipient.phone}
            </Typography>
          </Grid>
        </Grid>
      ),
    },
    {
      title: 'PRINT',
      dataIndex: 'printedAt',
      key: 'printedAt',
      align: 'center' as const,
      width: 100,
      render: (printedAt: number) => (
        <>
          <Typography variant="subtitle2" color="text.error">
            <Tag color={printedAt ? 'success' : 'error'}>
              {printedAt ? 'พิมพ์' : 'ยังไม่พิมพ์'}
            </Tag>
          </Typography>
          {printedAt && (
            <Typography
              sx={{ mt: 1 }}
              variant="subtitle2"
              color="text.secondary"
            >
              {dayjs(printedAt).format(ONLY_DATE_TIME_FORMAT)}
            </Typography>
          )}
        </>
      ),
    },
    {
      title: 'รหัส Tracking',
      dataIndex: 'trackingCode',
      key: 'trackingCode',
      width: 220,
      render: (trackingCode: string) => (
        <Typography variant="subtitle2" color="text.secondary">
          {trackingCode}
        </Typography>
      ),
    },
    {
      title: 'หมายเหตุ',
      dataIndex: 'error',
      key: 'error',
      width: 220,
      render: (error: string) => (
        <Typography variant="subtitle2" color="text.secondary">
          {error ?? ''}
        </Typography>
      ),
    },
  ]

  return (
    <Dialog open={isOpen} onClose={() => onClose(false)} maxWidth="md">
      <CircularLoading message={popupMessage} isLoading={loading} />
      <DialogTitle>ยกเลิก Tracking</DialogTitle>
      <DialogContent>
        <Box component="form" mt={1} onSubmit={formik.handleSubmit}>
          <Grid container spacing={2} justifyContent="space-between">
            <Grid item xs={12} lg={12}>
              <Table
                size={'middle'}
                columns={columns}
                pagination={false}
                dataSource={formik.values.orders?.map(
                  (tracking: any, index: number) => {
                    return {
                      ...tracking,
                      key: index,
                    }
                  },
                )}
                bordered
              ></Table>
            </Grid>
            <Grid item xs={12} lg={12}>
              <Grid container spacing={2} justifyContent="flex-end">
                <Grid item>
                  <Button
                    disabled={loading}
                    color="primary"
                    type="submit"
                    size="large"
                    variant="contained"
                    startIcon={<SendIcon />}
                  >
                    ยกเลิกทั้งหมด
                  </Button>
                </Grid>

                {/*<Grid item>*/}
                {/*  <Button*/}
                {/*    color="secondary"*/}
                {/*    type="submit"*/}
                {/*    size="large"*/}
                {/*    variant="contained"*/}
                {/*    startIcon={<SendIcon />}*/}
                {/*  >*/}
                {/*    ยืนยันยกเว้นพื้นที่พิเศษ*/}
                {/*  </Button>*/}
                {/*</Grid>*/}
                <Grid item>
                  <Button
                    color="inherit"
                    size="large"
                    variant="contained"
                    onClick={() => onClose(false)}
                    startIcon={<ClearIcon />}
                  >
                    ปิดหน้าต่าง
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
